'use client';

import React, { useRef } from 'react';
import Link from 'next/link';
import StackedHero from '@/components/our-work/StackedHero';

const projects = [
    {
        name: 'Design1Studio',
        tag: 'Real stories',
        image: '/images/our-work/design1studio-screenshot.webp',
        href: '/our-work/design1studio',
        ratio: '2872 / 1796',
    },
    {
        name: 'KOZA',
        tag: 'Real stories',
        image: '/images/our-work/koza-screenshot.webp',
        href: '/our-work/koza',
        ratio: '1280 / 889',
    },
    {
        name: 'Digital Circuits Pvt Ltd',
        tag: 'Website',
        image: '/images/our-work/digital-circuits-website.webp',
        href: '/our-work/digital-circuits',
        ratio: '2000 / 938',
    },
];

export default function WebDesignWork() {
    return (
        <main style={{ background: '#0f0f0f', minHeight: '100vh', overflow: 'clip' }}>

            <StackedHero
                title={<>Website Design &amp; Development</>}
                description="We design and build websites that do more than just look good — they perform. From user experience to responsive development, we create websites that engage visitors, build trust, and drive real business results."
            >

                {/* ── Project Cards ── */}
                <section
                    className="px-5 pt-14 pb-14 md:px-[95px] md:pt-20 md:pb-[120px]"
                    style={{
                        margin: '0 auto',
                        display: 'flex',
                        width: '100%',
                        maxWidth: 1920,
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 64,
                    }}>
                    {projects.map((project, i) => (
                        <ProjectCard key={project.name} project={project} index={i} />
                    ))}
                </section>
            </StackedHero>

        </main>
    );
}

function ProjectCard({ project }: { project: typeof projects[0]; index?: number }) {
    const cardRef = useRef<HTMLDivElement>(null);

    const imageCard = (
        <div
            className="project-card-img-wrap"
            style={{
                '--card-ratio': project.ratio,
                width: '100%',
                borderRadius: 40,
                overflow: 'hidden',
                background: '#e8e5df',
                position: 'relative',
                cursor: project.href ? 'pointer' : 'default',
                display: 'block',
                textDecoration: 'none',
            } as React.CSSProperties}
            onMouseEnter={e => {
                const img = (e.currentTarget as HTMLDivElement).querySelector('img') as HTMLImageElement;
                if (img) { img.style.transform = 'scale(1.03)'; img.style.transition = 'transform 0.6s ease'; }
            }}
            onMouseLeave={e => {
                const img = (e.currentTarget as HTMLDivElement).querySelector('img') as HTMLImageElement;
                if (img) { img.style.transform = 'scale(1)'; }
            }}
        >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
                src={project.image}
                alt={project.name}
                className="object-cover object-center md:object-top"
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    transition: 'transform 0.6s ease',
                }}
            />
            <style jsx>{`
                .project-card-img-wrap {
                    aspect-ratio: var(--card-ratio);
                    height: auto;
                }
                @media (min-width: 768px) {
                    .project-card-img-wrap {
                        aspect-ratio: auto;
                        height: clamp(300px, 42vw, 680px);
                    }
                }
            `}</style>
        </div>
    );

    return (
        <div
            ref={cardRef}
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
                width: '100%',
            }}
        >
            {project.href ? (
                <Link href={project.href} style={{ display: 'block', textDecoration: 'none' }}>
                    {imageCard}
                </Link>
            ) : imageCard}

            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 20px', paddingLeft: 8 }}>
                {project.href ? (
                    <Link href={project.href} style={{ textDecoration: 'none' }}>
                        <span style={{
                            fontFamily: '"Clash Display", sans-serif',
                            fontWeight: 400,
                            fontSize: 'clamp(18px, 2vw, 28px)',
                            color: '#080400',
                            letterSpacing: '0.02em',
                            whiteSpace: 'nowrap',
                        }}>
                            {project.name}
                        </span>
                    </Link>
                ) : (
                    <span style={{
                        fontFamily: '"Clash Display", sans-serif',
                        fontWeight: 400,
                        fontSize: 'clamp(18px, 2vw, 28px)',
                        color: '#080400',
                        letterSpacing: '0.02em',
                        whiteSpace: 'nowrap',
                    }}>
                        {project.name}
                    </span>
                )}

                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" style={{ flexShrink: 0 }}>
                    <line x1="38" y1="6" x2="6" y2="38" stroke="#080400" strokeWidth="2" strokeLinecap="round" />
                </svg>

                <span style={{
                    fontFamily: 'var(--font-manrope)',
                    fontWeight: 400,
                    fontSize: 'clamp(14px, 1.5vw, 20px)',
                    color: '#444',
                    letterSpacing: '0.02em',
                }}>
                    {project.tag}
                </span>
            </div>
        </div>
    );
}
